// @ts-nocheck
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useHotelPartnerId } from './useHotelPartnerId';

export interface OutreachRecipient {
  registration_id: string;
  coach_name: string;
  coach_email: string;
  team_name: string;
  has_booking: boolean;
  booking_status: string | null;
  hotel_radius_exempt: boolean;
}

export interface AssignedEvent {
  event_id: string;
  event_name: string;
  event_date: string;
  booking_url: string | null;
  group_rate_expiry: string | null;
}

export function useOutreachData() {
  const { data: partnerId } = useHotelPartnerId();

  const assignedEventsQuery = useQuery({
    queryKey: ['hotel-assigned-events-outreach', partnerId],
    enabled: !!partnerId,
    queryFn: async (): Promise<AssignedEvent[]> => {
      const { data, error } = await (supabase as any)
        .from('hotel_event_assignments')
        .select('event_id, booking_url, group_rate_expiry, events!inner(name, start_date)')
        .eq('hotel_partner_user_id', partnerId);
      if (error) throw error;
      return (data || []).map((d: any) => ({
        event_id: d.event_id,
        event_name: d.events?.name || '',
        event_date: d.events?.start_date || '',
        booking_url: d.booking_url,
        group_rate_expiry: d.group_rate_expiry,
      }));
    },
  });

  return { assignedEventsQuery, partnerId };
}

export function useEventTeams(eventId: string | null) {
  const { data: partnerId } = useHotelPartnerId();
  return useQuery({
    queryKey: ['hotel-outreach-teams', eventId],
    enabled: !!eventId && !!partnerId,
    queryFn: async (): Promise<OutreachRecipient[]> => {
      const { data, error } = await (supabase as any)
        .from('v_hotel_team_tracker')
        .select('registration_id, team_name, coach_name, coach_email, booking_status')
        .eq('event_id', eventId!);
      if (error) throw error;

      // Local-team exemption lives on the registration, not the tracker view.
      const regIds = (data || []).map((r: any) => r.registration_id).filter(Boolean);
      const exemptMap = new Map<string, boolean>();
      if (regIds.length > 0) {
        const { data: regs } = await (supabase as any)
          .from('event_registrations_v2')
          .select('id, hotel_radius_exempt')
          .in('id', regIds);
        for (const r of regs ?? []) exemptMap.set(r.id, !!r.hotel_radius_exempt);
      }

      return (data || []).map((r: any) => {
        const s = (r.booking_status || '').toLowerCase();
        return {
          registration_id: r.registration_id,
          coach_name: r.coach_name || '',
          coach_email: r.coach_email || '',
          team_name: r.team_name || '',
          has_booking: !!s && s !== 'pending' && s !== 'cancelled',
          booking_status: r.booking_status || null,
          hotel_radius_exempt: exemptMap.get(r.registration_id) ?? false,
        };
      });
    },
  });
}

export function useSentHistory(assignedEventIds: string[]) {
  const { data: partnerId } = useHotelPartnerId();
  const [eventFilter, setEventFilter] = useState<string>('all');
  const [dateFrom, setDateFrom] = useState<Date | undefined>();
  const [dateTo, setDateTo] = useState<Date | undefined>();

  const query = useQuery({
    queryKey: ['hotel-outreach-history', assignedEventIds, eventFilter, dateFrom?.toISOString(), dateTo?.toISOString()],
    enabled: assignedEventIds.length > 0 && !!partnerId,
    queryFn: async () => {
      let q = (supabase as any)
        .from('hotel_outreach_log')
        .select('*, events!inner(name)')
        .in('event_id', assignedEventIds)
        .order('sent_at', { ascending: false });

      if (eventFilter !== 'all') {
        q = q.eq('event_id', eventFilter);
      }
      if (dateFrom) {
        q = q.gte('sent_at', dateFrom.toISOString());
      }
      if (dateTo) {
        const end = new Date(dateTo);
        end.setHours(23, 59, 59, 999);
        q = q.lte('sent_at', end.toISOString());
      }

      const { data, error } = await q;
      if (error) throw error;
      const rows = data || [];

      // Sender names: hotel_outreach_log has no FK to profiles, so look them up.
      const senderIds = [...new Set(rows.map((r: any) => r.sent_by).filter(Boolean))];
      let senderMap = new Map<string, any>();
      if (senderIds.length > 0) {
        const { data: profs } = await (supabase as any)
          .from('profiles')
          .select('user_id, first_name, last_name, name')
          .in('user_id', senderIds);
        senderMap = new Map((profs || []).map((p: any) => [p.user_id, p]));
      }

      return rows.map((r: any) => ({ ...r, sender: senderMap.get(r.sent_by) || null }));
    },
  });

  return { query, eventFilter, setEventFilter, dateFrom, setDateFrom, dateTo, setDateTo };
}
